import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid2 as Grid,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CloseIcon from '@mui/icons-material/Close'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import userService from '../services/userService'
import projectService, {
  Project,
  ProjectUser,
} from '../services/projectService'
import api from '../services/authService'
import { CheckInOut, CheckInOutRecord } from '../types/checkinout'
import PageHeader from '../components/common/PageHeader'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { th } from 'date-fns/locale'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CheckInOutDetailDialogNew from '../components/dialogs/CheckInOutDetailDialogNew'
import DateFilter from '../components/common/Filters/DateFilter'
import RangeDateFilter from '../components/common/Filters/RangeDateFilter'

// ประเภทข้อมูลสำหรับ Check-in/out
interface Location {
  latitude: number
  longitude: number
  address: string
}

interface CheckInOutResponse {
  data: CheckInOutRecord[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

const EmployeeCheckInOutList: React.FC = () => {
  const navigate = useNavigate()
  const { username } = useParams<{ username: string }>()

  const [loading, setLoading] = useState(true)
  const [employee, setEmployee] = useState<any | null>(null)
  const [checkInOutRecords, setCheckInOutRecords] = useState<
    CheckInOutRecord[]
  >([])
  const [selectedProject, setSelectedProject] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const rowsPerPage = 10
  const [projects, setProjects] = useState<Project[]>([])
  const [projectsUser, setProjectsUser] = useState<ProjectUser[]>([])
  const [loadingProjects, setLoadingProjects] = useState(false)

  // สถานะสำหรับ Modal
  const [openModal, setOpenModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<CheckInOutRecord | null>(
    null,
  )
  const [openCheckInImage, setOpenCheckInImage] = useState(false)
  const [openCheckOutImage, setOpenCheckOutImage] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string>('')

  const [openNewModal, setOpenNewModal] = useState(false)
  const [selectedNewRecord, setSelectedNewRecord] =
    useState<CheckInOutRecord | null>(null)

  useEffect(() => {
    if (username) {
      fetchEmployeeData()
      fetchProjects()
      fetchCheckInOutData()
      fetchUserProjects()
    }
  }, [username, page])

  const fetchEmployeeData = async () => {
    try {
      setLoading(true)
      const userData = await userService.getUser(username || '')
      setEmployee(userData)
    } catch (error) {
      console.error('Error fetching employee data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true)
      const { projects: projectList } = await projectService.getProjects()
      setProjects(projectList)
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoadingProjects(false)
    }
  }

  const fetchCheckInOutData = async () => {
    try {
      setLoading(true)
      const response = await api.get<CheckInOutRecord[]>(
        `/project-checkins/txns`,
        {
          params: {
            page,
            perPage: rowsPerPage,
            projectId: selectedProject || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            username: username || undefined,
          },
        },
      )

      setCheckInOutRecords(response.data)

      // setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      console.error('Error fetching check-in/out data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserProjects = async () => {
    const response = await api.get<{ data: ProjectUser[] }>(
      `/projects-users/${username}`,
      {
        params: {
          page: 1,
          perPage: 10,
        },
      },
    )
    setProjectsUser(response?.data?.data || [])
  }

  const handleSearch = () => {
    setPage(1) // Reset to first page when searching
    fetchCheckInOutData()
  }

  const handleEditEmployee = (username: string) => {
    navigate(`/users/${username}/edit`, { state: { from: 'employees' } })
  }

  const handleProjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProject(event.target.value)
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStartDate(event.target.value)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value)
  }

  const handleClearFilters = () => {
    setSelectedProject('')
    setStartDate('')
    setEndDate('')
  }

  const handleViewDetails = (record: CheckInOutRecord) => {
    setSelectedRecord(record)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedRecord(null)
  }

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage)
  }

  const handleViewImage = (imageUrl: string, isCheckIn: boolean) => {
    setSelectedImage(imageUrl)
    if (isCheckIn) {
      setOpenCheckInImage(true)
    } else {
      setOpenCheckOutImage(true)
    }
  }

  const handleViewNewDetails = (record: CheckInOutRecord) => {
    setSelectedNewRecord(record)
    setOpenNewModal(true)
  }

  const handleCloseNewModal = () => {
    setOpenNewModal(false)
    setSelectedNewRecord(null)
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  // Format time for display
  const formatTime = (dateString: string | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateWithTime = (dateString: string | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Format status for display
  const formatStatus = (status: string) => {
    switch (status) {
      case 'checkin':
        return 'เข้างาน'
      case 'checkout':
        return 'ออกงาน'
      default:
        return '-'
    }
  }

  // Calculate maximum number of check-in/out times
  const getMaxCheckInOutTimes = (records: CheckInOut[]) => {
    const maxCheckIn = Math.max(
      ...records.map((record) => record.checkInTimes.length),
    )
    const maxCheckOut = Math.max(
      ...records.map((record) => record.checkOutTimes.length),
    )
    return { maxCheckIn, maxCheckOut }
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: '100%',
        bgcolor: '#FFFFFF',
      }}
    >
      <PageHeader
        title="ข้อมูลพนักงาน"
        breadcrumb={{
          items: [
            { label: 'รายการพนักงาน', onClick: () => navigate('/employees') },
            { label: 'ข้อมูลพนักงาน' },
          ],
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          mt: 8,
          mb: { xs: 2, sm: 2, lg: 4 },
          gap: 2,
        }}
      >
        {loading && !employee ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : employee ? (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                gap: 1,
                p: 1,
                pl: 2,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                width: { md: '50%' },
              }}
            >
              <Box>
                <Box sx={{ flexGrow: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      ข้อมูลพนักงาน : {employee.name}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        handleEditEmployee(employee.username)
                      }}
                    >
                      <Box
                        component="img"
                        src="/images/icons/button-modify.png"
                        alt="View"
                        sx={{ width: 32, height: 32 }}
                      />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="custom.grey">
                    รหัสพนักงาน: {employee.employeeCode || '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 2,
                    gap: 4,
                  }}
                >
                  <Box
                    component="img"
                    src={employee.image || '/images/avatar-placeholder.jpg'}
                    alt={employee.name}
                    sx={{
                      width: { xs: 120, md: 120, lg: 120 },
                      height: { xs: 120, md: 120, lg: 120 },
                      borderRadius: '4px',
                      objectFit: 'cover',
                      bgcolor: '#f5f5f5',
                    }}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <Box sx={{ width: { md: '50%', lg: '28%' } }}>
                      <Typography variant="body2" sx={{ fontWeight: 300 }}>
                        ชื่อพนักงาน :
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {employee.name}
                      </Typography>
                    </Box>{' '}
                    <Box sx={{ width: { md: '50%', lg: '28%' } }}>
                      <Typography variant="body2" sx={{ fontWeight: 300 }}>
                        เบอร์โทร :
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {employee.phoneNumber || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: { md: '50%', lg: '28%' } }}>
                      <Typography variant="body2" sx={{ fontWeight: 300 }}>
                        email :
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {employee.email || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: { md: '50%', lg: '28%' } }}>
                      <Typography variant="body2" color="text.secondary">
                        สถานะการใช้งาน :
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: employee.isEnable
                            ? 'success.main'
                            : 'error.main',
                          fontWeight: 500,
                        }}
                      >
                        {employee.isEnable ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: { md: '50%', lg: '28%' } }}>
                      <Typography variant="body2" color="text.secondary">
                        Username :
                      </Typography>
                      <Typography variant="body1">
                        {employee.username}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                flexGrow: 1,
                gap: 1,
                p: 1,
                pl: 2,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ flexGrow: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    โครงการทั้งหมด : {employee.name}
                  </Typography>{' '}
                  <IconButton onClick={() => {}}>
                    <Box sx={{ width: 32, height: 32 }} />
                  </IconButton>
                </Box>
                <TableContainer
                  sx={{
                    maxWidth: '100%',
                    overflowX: 'auto',
                    '& .MuiTableCell-root': {
                      py: 1.5,
                      px: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    },
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ลำดับ</TableCell>
                        <TableCell>รหัสโครงการ</TableCell>
                        <TableCell>ชื่อโครงการ</TableCell>
                        <TableCell>สถานะการใช้งาน</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projectsUser?.map((project, index) => (
                        <TableRow key={project._id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{project.code}</TableCell>
                          <TableCell>{project.name}</TableCell>
                          <TableCell>
                            {project.isActive
                              ? 'เปิดการใช้งาน'
                              : 'ปิดการใช้งาน'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Typography variant="h6" color="error" sx={{ my: 4 }}>
            ไม่พบข้อมูลพนักงาน
          </Typography>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: { xs: 'end', sm: 'start' }, alignItems: 'end' }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              โครงการ
            </Typography>
            <TextField
              key={selectedProject}
              select
              fullWidth
              label={selectedProject ? '' : 'โครงการทั้งหมด'}
              value={selectedProject ? selectedProject : ''}
              onChange={handleProjectChange}
              disabled={loadingProjects}
              slotProps={{
                inputLabel: {
                  shrink: false,
                },
              }}
            >
              <MenuItem value="">โครงการทั้งหมด</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.objectId} value={project.objectId}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <RangeDateFilter
              onStartDateChange={(date) => {
                setStartDate(date)
              }}
              onEndDateChange={(date) => {
                setEndDate(date)
              }}
            />
          </Grid>
          <Grid >
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              ค้นหา
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">ลำดับ</TableCell>
                <TableCell align="center">วันที่</TableCell>
                <TableCell>ชื่อพนักงาน</TableCell>
                <TableCell align="center">โครงการเข้างาน</TableCell>
                <TableCell align="center">เวลาเข้างาน</TableCell>
                <TableCell align="center">โครงการออกงาน 1</TableCell>
                <TableCell align="center">เวลาออกงาน 1</TableCell>
                <TableCell align="center">โครงการออกงาน 2</TableCell>
                <TableCell align="center">เวลาออกงาน 2</TableCell>
                <TableCell align="center">รายละเอียด</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : checkInOutRecords?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                      ไม่พบข้อมูล
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      กรุณาเลือกโครงการและช่วงวันที่ที่ต้องการดูข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                checkInOutRecords?.map((record, index) => {
                  // Get the first and last checkout records
                  const firstCheckout =
                    record.checkout && record.checkout.length > 0
                      ? record.checkout[0]
                      : null
                  const lastCheckout =
                    record.checkout && record.checkout.length > 1
                      ? record.checkout[record.checkout.length - 1]
                      : null

                  return (
                    <TableRow key={record._id || index}>
                      <TableCell align="center">
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(record.checkin.activeTime)}
                      </TableCell>
                      <TableCell>
                        {record.checkin?.user?.[0]?.name || record.username}
                      </TableCell>
                      <TableCell align="center">
                        {record.checkin?.project?.name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {record.checkin?.activeTime
                          ? formatTime(record.checkin.activeTime)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {firstCheckout?.project?.name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {firstCheckout?.activeTime
                          ? formatDateWithTime(firstCheckout.activeTime)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {lastCheckout?.project?.name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {lastCheckout?.activeTime
                          ? formatDateWithTime(lastCheckout.activeTime)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedRecord(record)
                            setOpenModal(true)
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal แสดงรายละเอียดการเข้า-ออกงาน */}
      {selectedRecord && (
        <CheckInOutDetailDialogNew
          open={openModal}
          onClose={() => {
            setOpenModal(false)
            setSelectedRecord(null)
          }}
          selectedRecord={selectedRecord}
          onImageClick={(image, isCheckIn) => {
            setSelectedImage(image)
            if (isCheckIn) {
              setOpenCheckInImage(true)
            } else {
              setOpenCheckOutImage(true)
            }
          }}
        />
      )}

      {/* Dialog แสดงรูปภาพขนาดใหญ่ */}
      <Dialog
        open={openCheckInImage || openCheckOutImage}
        onClose={() => {
          setOpenCheckInImage(false)
          setOpenCheckOutImage(false)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 1 }}>
          <Box
            component="img"
            src={selectedImage}
            alt="Check-in/out"
            sx={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCheckInImage(false)
              setOpenCheckOutImage(false)
            }}
          >
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EmployeeCheckInOutList
